/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, December 25th 2021, 11:38:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useAPIFetchPaginatedList, FetchPaginatedListArgs } from "lib/api/hooks/useAPIFetchPaginatedList";

/**
 *
 * Works with Django REST API Paginated Response to lazy load a list of items. As part of its return the hook
 * includes a lazyLoadData function that goes to the next page.
 *
 */

enum LAZY_LOAD_STATE_ACTIONS {
  UPDATE_CURRENT_ITEMS = "update_current_items",
  RESET_CURRENT_ITEMS = "reset_current_items",
  SET_REMAINING_COUNT = "set_remaining_count", // since the number of items to display on a screen at any given time can be stateful depending on the UI, device etc, we need an action to dynamically set this, especially initially when the number of items to be fetched is unknown.
}

interface LazyLoadState<T> {
  currentItems: T[];
  hasMoreItems: boolean;
  remainingItemsCount: number;
  isLoadingAfresh: boolean;
}

interface UpdateCurrentItemsAction<T> {
  type: LAZY_LOAD_STATE_ACTIONS.UPDATE_CURRENT_ITEMS;
  payload: { latestFetchItems: T[]; apiTotalItems: number };
}

interface ResetCurrentItemsAction {
  type: LAZY_LOAD_STATE_ACTIONS.RESET_CURRENT_ITEMS;
  payload: { apiPageLimit: FetchPaginatedListArgs<any>["apiPageLimit"] };
}

interface SetRemainingCountAction {
  type: LAZY_LOAD_STATE_ACTIONS.SET_REMAINING_COUNT;
  payload: { remainingItemsCount: number };
}

type LazyLoadAction<T> = UpdateCurrentItemsAction<T> | ResetCurrentItemsAction | SetRemainingCountAction;

type LazyLoadReducer<T> = (previousState: LazyLoadState<T>, action: LazyLoadAction<T>) => LazyLoadState<T>;

const lazyLoadReducer = <T>(previousState: LazyLoadState<T>, action: LazyLoadAction<T>): LazyLoadState<T> => {
  if (action.type === LAZY_LOAD_STATE_ACTIONS.UPDATE_CURRENT_ITEMS && Array.isArray(action.payload.latestFetchItems)) {
    const updatedItems = [...previousState.currentItems, ...action.payload.latestFetchItems];

    return {
      currentItems: updatedItems,
      hasMoreItems: action.payload.apiTotalItems > updatedItems.length,
      remainingItemsCount: action.payload.apiTotalItems - updatedItems.length,
      isLoadingAfresh: false,
    };
  }

  if (action.type === LAZY_LOAD_STATE_ACTIONS.RESET_CURRENT_ITEMS) {
    return { ...previousState, currentItems: [], hasMoreItems: true, remainingItemsCount: action.payload.apiPageLimit };
  }

  if (action.type === LAZY_LOAD_STATE_ACTIONS.SET_REMAINING_COUNT) {
    return { ...previousState, remainingItemsCount: action.payload.remainingItemsCount };
  }

  return previousState;
};

export type UseLazyFetchListArgs<T> = FetchPaginatedListArgs<T>;

export const useAPILazyFetchList = <T>(args: UseLazyFetchListArgs<T>) => {
  const {
    apiTotalItems,
    apiPageOffset,
    setAPIPageOffset,
    items: latestFetchItems,
    ...otherProperites
  } = useAPIFetchPaginatedList<T>(args);

  const { apiPageLimit, dependencyList } = args;

  const [lazyState, dispatchLazyAction] = React.useReducer<LazyLoadReducer<T>>(lazyLoadReducer, {
    currentItems: [],
    hasMoreItems: true,
    remainingItemsCount: apiPageLimit ?? 0,
    isLoadingAfresh: otherProperites.isMakingAPIRequest,
  });

  // doInitialFetch function to be used if makeRequestOnInitialFetch is false
  const doInitialFetch = React.useCallback(() => {
    setAPIPageOffset(0); // Always start a lazy load from page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Reset the current items when our dependency list changes because this means its a new API fetch session.
    dispatchLazyAction({ type: LAZY_LOAD_STATE_ACTIONS.RESET_CURRENT_ITEMS, payload: { apiPageLimit } });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencyList]);

  // Function to load additional data after inital fetch is done
  const lazyLoadData = React.useCallback(() => {
    if (!apiPageLimit || !lazyState.hasMoreItems) return;

    setAPIPageOffset((previous_offset) => previous_offset + apiPageLimit);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPageLimit, lazyState.hasMoreItems]);

  React.useEffect(() => {
    if (latestFetchItems.length > 0 && apiTotalItems > 0) {
      dispatchLazyAction({
        type: LAZY_LOAD_STATE_ACTIONS.UPDATE_CURRENT_ITEMS,
        payload: { apiTotalItems, latestFetchItems },
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchLazyAction, apiTotalItems, JSON.stringify(latestFetchItems)]);

  React.useEffect(() => {
    dispatchLazyAction({
      type: LAZY_LOAD_STATE_ACTIONS.SET_REMAINING_COUNT,
      payload: { remainingItemsCount: apiPageLimit },
    });
  }, [apiPageLimit, dispatchLazyAction]);

  return { doInitialFetch, lazyLoadData, apiTotalItems, ...lazyState, ...otherProperites };
};
